import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import api from '../services/api';
import { getToken } from '../services/auth';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';

// Hashed build files and Addressables bundles never change, so the browser cache can keep them.
export const resolveCacheControl = (url = '') => (
  /\.(bundle|data|wasm|framework\.js)(\.br)?(\?|$)/i.test(url) ? 'immutable' : 'must-revalidate'
);

export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const supportsWebGpu = async () => {
  if (typeof navigator === 'undefined' || !navigator.gpu) return false;
  try {
    return Boolean(await navigator.gpu.requestAdapter());
  } catch {
    return false;
  }
};

const formatMegabytes = (bytes) => `${Math.max(1, Math.round((bytes || 0) / (1024 * 1024)))} MB`;

const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[data-vm-web-player="${src}"]`);
  if (existing) {
    resolve();
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.dataset.vmWebPlayer = src;
  script.onload = () => resolve();
  script.onerror = () => reject(new Error('The Virtual Medic player could not be downloaded.'));
  document.body.appendChild(script);
});

function DownloadsBanner() {
  return (
    <Alert
      severity="info"
      action={(
        <Button component={RouterLink} to="/downloads" startIcon={<DownloadIcon />} sx={publicSecondaryButtonSx}>
          Downloads
        </Button>
      )}
    >
      This is a test version of Virtual Medic that runs in your browser. Downloading the app gives a better
      experience: sharper characters, faster loading and more reliable voice.
    </Alert>
  );
}

function WebPlayerPage() {
  const [searchParams] = useSearchParams();
  const requestedBuildId = searchParams.get('buildId') || '';
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const [manifest, setManifest] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (isMobileDevice()) {
        setStatus('mobile');
        return;
      }
      if (!(await supportsWebGpu())) {
        if (!cancelled) setStatus('unsupported');
        return;
      }
      try {
        const response = await api.get('/web-player/current', {
          params: requestedBuildId ? { buildId: requestedBuildId } : undefined,
        });
        if (cancelled) return;
        setManifest(response.data?.build || null);
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        setMessage(error?.response?.data?.message || 'The browser version is not available right now.');
        setStatus('unavailable');
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [requestedBuildId]);

  useEffect(() => () => {
    const instance = instanceRef.current;
    instanceRef.current = null;
    if (instance) {
      instance.Quit().catch(() => {});
    }
    delete window.VirtualMedicWebPlayer;
  }, []);

  const start = useCallback(async () => {
    if (!manifest || !canvasRef.current) return;
    setStatus('loading');
    setProgress(0);
    setMessage('');

    // Ask the browser to keep the downloaded characters instead of clearing them under storage pressure.
    navigator.storage?.persist?.().catch(() => {});

    // Read by the player (Assets/Plugins/WebGL/VirtualMedicWebHost.jslib).
    window.VirtualMedicWebPlayer = {
      getSessionToken: () => getToken() || '',
      openPage: (url) => window.open(url, '_blank', 'noopener'),
    };

    try {
      await loadScript(manifest.loaderUrl);
      if (typeof window.createUnityInstance !== 'function') {
        throw new Error('The Virtual Medic player did not start.');
      }
      instanceRef.current = await window.createUnityInstance(canvasRef.current, {
        dataUrl: manifest.dataUrl,
        frameworkUrl: manifest.frameworkUrl,
        codeUrl: manifest.codeUrl,
        streamingAssetsUrl: manifest.streamingAssetsUrl,
        companyName: 'Virtual Medic',
        productName: 'Virtual Medic',
        productVersion: manifest.productVersion,
        // Rendering at full Retina/4K density costs a lot of GPU time in the browser.
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
        cacheControl: resolveCacheControl,
        showBanner: (text, type) => {
          if (type === 'error') setMessage(text);
        },
      }, (value) => setProgress(Math.round(value * 100)));
      setStatus('running');
    } catch (error) {
      setMessage(error?.message || 'The Virtual Medic player could not start.');
      setStatus('error');
    }
  }, [manifest]);

  const isPlayerVisible = status === 'loading' || status === 'running';

  return (
    <PublicPageFrame containerMaxWidth="xl" contentMaxWidth={1400}>
      <PublicCardHeader
        eyebrow="Beta"
        title="Virtual Medic in your browser"
        description="Run consultations without installing anything. Use a desktop computer with a microphone."
      />

      <Stack spacing={2.5}>
        <DownloadsBanner />

        {manifest && !manifest.isLive && (
          <Alert severity="warning">
            Previewing draft build {manifest.version}{manifest.buildNumber ? ` (${manifest.buildNumber})` : ''}. Users cannot see it until it is published.
          </Alert>
        )}

        {status === 'checking' && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>Checking your browser...</Typography>
          </Stack>
        )}

        {status === 'mobile' && (
          <Alert severity="warning">
            The browser version is for desktop computers. On a phone or tablet, download the Virtual Medic app instead.
          </Alert>
        )}

        {status === 'unsupported' && (
          <Alert severity="warning">
            Your browser does not support WebGPU, which the browser version needs. Use a current version of Chrome or Edge,
            or download the app.
          </Alert>
        )}

        {(status === 'unavailable' || status === 'error') && message && <Alert severity="error">{message}</Alert>}

        {status === 'ready' && manifest && (
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
              The first start downloads about {formatMegabytes(manifest.totalBytes)}, plus each patient the first time you meet them.
              Later visits load from your browser&apos;s cache. Your browser will ask to use the microphone when a consultation starts.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={start} sx={publicPrimaryButtonSx}>
                Start Virtual Medic
              </Button>
              <Chip size="small" label={`Version ${manifest.version}`} />
            </Stack>
          </Stack>
        )}

        <Box
          sx={{
            display: isPlayerVisible ? 'block' : 'none',
            position: 'relative',
            width: '100%',
            height: 'min(78vh, calc(100vw * 9 / 16))',
            borderRadius: '20px',
            overflow: 'hidden',
            bgcolor: '#0b1724',
            border: `1px solid ${alpha(publicPalette.heroBlueMid, 0.18)}`,
          }}
        >
          <canvas
            ref={canvasRef}
            id="virtual-medic-web-player"
            tabIndex={-1}
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
          {status === 'loading' && (
            <Stack spacing={1} sx={{ position: 'absolute', left: 24, right: 24, bottom: 24 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography sx={{ color: '#ffffff', fontFamily: publicBodyFontFamily }}>Loading Virtual Medic... {progress}%</Typography>
            </Stack>
          )}
        </Box>

        {status === 'running' && (
          <Box>
            <Button
              startIcon={<FullscreenIcon />}
              onClick={() => instanceRef.current?.SetFullscreen(1)}
              sx={publicSecondaryButtonSx}
            >
              Full screen
            </Button>
          </Box>
        )}
      </Stack>
    </PublicPageFrame>
  );
}

export default WebPlayerPage;
